import React, { useState, useContext, useRef } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { FaArrowRight, FaFingerprint, FaLock, FaShieldAlt } from "react-icons/fa";
import { AuthContext } from "../../context/AuthContext";
import "./LoginPage.css";

const LOCAL_API_BASE_URL = "http://localhost:5000";
const RENDER_API_BASE_URL = "https://wecareehs-backend.onrender.com";
const API_BASE_URL =
  process.env.REACT_APP_USE_RENDER === "true" ? RENDER_API_BASE_URL : LOCAL_API_BASE_URL;

const MAX_CLIENT_ATTEMPTS = 5;
const CLIENT_COOLDOWN_MS = 60 * 1000; // 1 minute client-side cooldown

const LoginPage = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [message, setMessage] = useState("");
  const [messageType, setMessageType] = useState(""); // "error" | "success"
  const [loading, setLoading] = useState(false);

  // Client-side attempt limiter (additional UI layer)
  const attemptCount = useRef(0);
  const cooldownUntil = useRef(null);
  const [cooldownSeconds, setCooldownSeconds] = useState(0);
  const cooldownInterval = useRef(null);

  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useContext(AuthContext);

  const isOnCooldown = () => {
    if (!cooldownUntil.current) return false;
    return Date.now() < cooldownUntil.current;
  };

  const startCooldown = () => {
    cooldownUntil.current = Date.now() + CLIENT_COOLDOWN_MS;
    setCooldownSeconds(Math.ceil(CLIENT_COOLDOWN_MS / 1000));

    cooldownInterval.current = setInterval(() => {
      const remaining = Math.ceil((cooldownUntil.current - Date.now()) / 1000);
      if (remaining <= 0) {
        clearInterval(cooldownInterval.current);
        cooldownUntil.current = null;
        attemptCount.current = 0;
        setCooldownSeconds(0);
      } else {
        setCooldownSeconds(remaining);
      }
    }, 1000);
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setMessage("");

    if (isOnCooldown()) {
      setMessage(`Too many attempts. Please wait ${cooldownSeconds} second(s) before trying again.`);
      setMessageType("error");
      return;
    }

    setLoading(true);

    try {
      const res = await fetch(`${API_BASE_URL}/api/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.trim().toLowerCase(), password }),
      });
      const data = await res.json();

      if (!res.ok) {
        attemptCount.current += 1;
        if (attemptCount.current >= MAX_CLIENT_ATTEMPTS) {
          startCooldown();
          setMessage(`Too many failed attempts. Please wait 1 minute before trying again.`);
        } else {
          const remaining = MAX_CLIENT_ATTEMPTS - attemptCount.current;
          setMessage(
            `${data.error || "Invalid credentials."} (${remaining} attempt${remaining !== 1 ? "s" : ""} remaining)`
          );
        }
        setMessageType("error");
      } else {
        attemptCount.current = 0;
        login(data.token, data.user);
        const from = location.state?.from || "/dashboard";
        navigate(from, { replace: true });
      }
    } catch (err) {
      console.error(err);
      setMessage("Unable to connect. Please check your connection and try again.");
      setMessageType("error");
    } finally {
      setLoading(false);
    }
  };

  const isSubmitDisabled = loading || isOnCooldown() || cooldownSeconds > 0;

  return (
    <div className="split-login-container">
      {/* Left Visual Panel */}
      <div className="visual-panel">
        <div className="visual-content">
          <h1 className="visual-heading">
            Future
            <br />
            Secured.
          </h1>
          <p className="visual-subtext">
            Access the next generation of Futuristic Safety.
          </p>
          <div className="security-badge">
            <FaShieldAlt />
            <span>256-bit Encrypted Session</span>
          </div>
        </div>
        <div className="abstract-shape"></div>
        <div className="abstract-shape-2"></div>
      </div>

      {/* Right Form Panel */}
      <div className="form-panel">
        <div className="form-content">
          <div className="login-header">
            <h2>Hello Again.</h2>
            <p>Enter your credentials to access your account.</p>
          </div>

          <form onSubmit={handleLogin} className="split-form" autoComplete="on">
            <div className="minimal-input-group">
              <label htmlFor="login-email">Email</label>
              <input
                id="login-email"
                type="email"
                className="minimal-input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="name@example.com"
                autoComplete="email"
                required
                disabled={isSubmitDisabled}
              />
            </div>

            <div className="minimal-input-group">
              <div className="label-row">
                <label htmlFor="login-password">Password</label>
                <Link to="/forgot-password" className="forgot-mini-link">
                  Forgot?
                </Link>
              </div>
              <input
                id="login-password"
                type="password"
                className="minimal-input"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                autoComplete="current-password"
                required
                disabled={isSubmitDisabled}
              />
            </div>

            {message && (
              <div className={`status-message ${messageType === "error" ? "error" : ""}`}>
                {messageType === "error" && <FaLock style={{ marginRight: "6px" }} />}
                {message}
              </div>
            )}

            {cooldownSeconds > 0 && (
              <div className="cooldown-bar">
                <div
                  className="cooldown-fill"
                  style={{
                    width: `${(cooldownSeconds / (CLIENT_COOLDOWN_MS / 1000)) * 100}%`,
                  }}
                />
              </div>
            )}

            <button
              type="submit"
              className="action-btn"
              disabled={isSubmitDisabled}
              aria-label="Sign in to your account"
            >
              {loading
                ? "Verifying..."
                : cooldownSeconds > 0
                ? `Wait ${cooldownSeconds}s`
                : "Sign In"}{" "}
              {!loading && cooldownSeconds === 0 && <FaArrowRight />}
            </button>
          </form>

          <div className="panel-footer">
            <p>
              New here?{" "}
              <Link to="/register" className="highlight-link">
                Create Account
              </Link>
            </p>
            <button
              className="admin-ghost-btn"
              onClick={() => navigate("/admin/login")}
              type="button"
            >
              <FaFingerprint size={14} /> Admin
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
